import { useCallback, useEffect, useRef } from 'react';
import { useInterviewStore } from '../stores/interview-store.js';
import { get, post, ApiClientError } from '../lib/api-client.js';
import type { ApiResponse } from '../types/api.js';
import type { InterviewSession } from '../types/interview.js';
import i18n from '../lib/i18n.js';

/** Stable reference to Zustand store actions (not affected by state changes) */
const getStoreActions = () => useInterviewStore.getState();

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

/** Backend start response shape */
interface StartResponse {
  session_id: string;
  respondent_id: string;
  first_message: string;
  questions: Array<{ id: string; text: string; type: string; order_index: number }>;
}

/** Backend status response shape */
interface StatusResponse {
  survey_id: string;
  status: string;
  title: string;
  question_count: number;
  current_respondents: number;
  max_respondents: number;
}

/** Parsed result from SSE message_complete event */
interface SSEResult {
  fullMessage: string;
  advance: boolean;
}

/**
 * Parse SSE stream from /messages endpoint and return the full AI message + advance flag.
 */
async function parseSSEResponse(response: Response): Promise<SSEResult> {
  const text = await response.text();
  const lines = text.split('\n');
  let fullMessage = '';
  let advance = true;

  for (const line of lines) {
    if (line.startsWith('data: ')) {
      try {
        const data = JSON.parse(line.slice(6));
        if (data.full_message) {
          fullMessage = data.full_message;
        }
        if (typeof data.advance === 'boolean') {
          advance = data.advance;
        }
      } catch {
        // skip non-JSON data lines
      }
    }
  }

  return { fullMessage, advance };
}

/** Backend locale update response shape */
interface LocaleResponse {
  greeting: string;
  questions: Array<{ id: string; text: string; type: string; order_index: number }>;
}

export function useInterview(token: string) {
  const store = useInterviewStore();
  const sessionIdRef = useRef<string | null>(null);
  const questionsRef = useRef<StartResponse['questions']>([]);
  const questionIndexRef = useRef(0);
  const startingRef = useRef(false);
  const currentLocaleRef = useRef<string>((i18n.language ?? 'ja').substring(0, 2));

  useEffect(() => {
    store.setToken(token);
  }, [token, store.setToken]);

  // Watch for language changes and update greeting + session locale.
  // Uses refs and getState() to avoid depending on `store` (which changes on every
  // state update) — this prevents the listener from being torn down and re-registered
  // during the same React render cycle that i18n.changeLanguage triggers,
  // which would cause the 'languageChanged' event to be missed.
  const tokenRef = useRef(token);
  tokenRef.current = token;

  useEffect(() => {
    const handleLanguageChanged = async (lng: string) => {
      const newLocale = lng.substring(0, 2) as 'ja' | 'en';
      if (newLocale === currentLocaleRef.current) return;
      if (!sessionIdRef.current) return;

      currentLocaleRef.current = newLocale;

      try {
        const res = await post<ApiResponse<LocaleResponse>>(
          `/api/v1/interviews/${tokenRef.current}/locale`,
          { session_id: sessionIdRef.current, locale: newLocale },
        );
        // Update greeting (first AI message) via stable store reference
        getStoreActions().updateFirstAiMessage(res.data.greeting);
        // Update translated questions
        questionsRef.current = res.data.questions;
      } catch {
        // Locale update is best-effort; don't show error to user
      }
    };

    i18n.on('languageChanged', handleLanguageChanged);
    return () => {
      i18n.off('languageChanged', handleLanguageChanged);
    };
  }, []);

  const startInterview = useCallback(async () => {
    // Guard against double-invocation (React Strict Mode)
    if (startingRef.current) return;
    startingRef.current = true;

    try {
      store.setStatus('loading');
      const locale = (i18n.language ?? 'ja').substring(0, 2) as 'ja' | 'en';
      const res = await post<ApiResponse<StartResponse>>(`/api/v1/interviews/${token}/start`, {
        consent: true,
        locale,
      });
      sessionIdRef.current = res.data.session_id;
      questionsRef.current = res.data.questions;
      questionIndexRef.current = 0;
      store.setSurveyId(res.data.session_id);
      store.setStatus('chatting');

      // Show greeting as AI message (first question is introduced naturally by AI)
      const greeting = res.data.first_message ?? '';
      store.addMessage({ role: 'ai', content: greeting });
    } catch (err) {
      startingRef.current = false;
      const message = err instanceof ApiClientError ? err.message : 'Failed to start interview';
      store.setError(message);
    }
  }, [token, store]);

  const sendMessage = useCallback(
    async (text: string) => {
      store.addMessage({ role: 'user', content: text });

      try {
        const currentIdx = questionIndexRef.current;
        const url = `${BASE_URL}/api/v1/interviews/${token}/messages`;
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            message: text,
            question_index: currentIdx,
          }),
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status}`);
        }

        // Parse the SSE response to extract the AI reply and advance flag
        const { fullMessage: aiMessage, advance } = await parseSSEResponse(response);

        // Show AI response
        if (aiMessage) {
          store.addMessage({ role: 'ai', content: aiMessage });
        }

        if (advance) {
          // Advance to next question (index tracked internally)
          questionIndexRef.current = currentIdx + 1;
          const nextIdx = questionIndexRef.current;
          const questions = questionsRef.current;

          if (nextIdx >= questions.length) {
            // All questions answered — auto-complete
            store.setStatus('completing');
            await post<ApiResponse<void>>(`/api/v1/interviews/${token}/complete`, {
              session_id: sessionIdRef.current,
            });
            store.setStatus('completed');
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send message';
        store.setError(message);
      }
    },
    [token, store],
  );

  const completeInterview = useCallback(async () => {
    try {
      store.setStatus('completing');
      await post<ApiResponse<void>>(`/api/v1/interviews/${token}/complete`, {
        session_id: sessionIdRef.current,
      });
      store.setStatus('completed');
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to complete interview';
      store.setError(message);
    }
  }, [token, store]);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await get<ApiResponse<StatusResponse>>(`/api/v1/interviews/${token}/status`);
      return res.data as unknown as InterviewSession;
    } catch (err) {
      const message = err instanceof ApiClientError ? err.message : 'Failed to fetch status';
      store.setError(message);
      return null;
    }
  }, [token, store]);

  return {
    messages: store.messages,
    currentQuestion: questionIndexRef.current,
    totalQuestions: questionsRef.current.length,
    status: store.status,
    sendMessage,
    startInterview,
    completeInterview,
    fetchStatus,
    error: store.error,
  };
}
